import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress, Avatar, Paper, Chip, AppBar, Toolbar, IconButton, Tooltip } from '@mui/material';
import { keyframes } from '@mui/system';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTeluguYear } from '../context/TeluguYearContext';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

// Animation keyframes
const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-50px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(50px); }
  to { opacity: 1; transform: translateX(0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.02); }
  100% { transform: scale(1); }
`;

// Background themes for each slide
const slideBackgrounds = [
  // Slide 1: Soft gradient with subtle pattern
  'linear-gradient(135deg, rgba(127,75,45,0.05) 0%, rgba(0,64,128,0.08) 50%, rgba(127,75,45,0.05) 100%)',
  // Slide 2: Light blue gradient
  'linear-gradient(135deg, rgba(227,242,253,0.9) 0%, rgba(187,222,251,0.9) 50%, rgba(144,202,249,0.7) 100%)',
  // Slide 3: Warm gradient
  'linear-gradient(135deg, rgba(255,248,225,0.9) 0%, rgba(255,236,179,0.9) 50%, rgba(255,224,130,0.7) 100%)',
  // Slide 4: Professional gradient
  'linear-gradient(135deg, rgba(232,245,233,0.9) 0%, rgba(200,230,201,0.9) 50%, rgba(165,214,167,0.7) 100%)'
];

const labels = {
  telugu: {
    title: 'వేదారణ్యమ్ పరీక్ష',
    parikshaBatches: 'పరీక్షా బ్యాచ్‌లు',
    antyakshariBatches: 'పనసల అంత్యాక్షరి బ్యాచ్‌లు',
    topPerformers: 'ఈ రోజు అగ్ర ప్రదర్శకులు',
    batch: 'బ్యాచ్',
    vatus: 'వటువులు',
    noData: 'డేటా అందుబాటులో లేదు',
    loading: 'లోడవుతోంది...',
    junior: 'జూనియర్',
    senior: 'సీనియర్',
    percentage: 'శాతం',
    pathasala: 'పాఠశాల',
    photos: 'ఫోటోలు',
    athyuthama_vidyardhi: ' అత్యుత్తమ విద్యార్థి',
    anthyakshari_winner: 'నిన్నటి అంత్యాక్షరీ విజేత'
  },
  sanskrit: {
    title: 'वेदारण्यम् परीक्षा',
    parikshaBatches: 'परीक्षा समूहाः',
    antyakshariBatches: 'पनसाला अन्त्याक्षरी समूहाः',
    topPerformers: 'उत्तम प्रदर्शकाः',
    batch: 'समूहः',
    vatus: 'वाटवः',
    noData: 'दत्तांशः नास्ति',
    loading: 'आरोप्यते...',
    junior: 'कनिष्ठः',
    senior: 'ज्येष्ठः',
    percentage: 'प्रतिशतम्',
    pathasala: 'पाठशाला',
    photos: 'छायाचित्राणि',
    athyuthama_vidyardhi: 'अत्युत्तमविद्यार्थी',
    anthyakshari_winner: 'ह्यस्तादिन न्त्याक्षरीविजेता'
  }
};

// Slide 0: Featured Photos (athyuthama_vidyardhi + anthyakshari winners)
// Supports displaying 1, 2, 3, or 4 photos in a responsive grid
const PhotoSlide = ({ photos, lang }) => {
  const currentLabels = labels[lang];
  
  // Determine layout based on number of photos
  const getPhotoLayout = () => {
    if (!photos || photos.length === 0) return { gap: 8, cols: 1 };
    if (photos.length === 1) return { gap: 8, cols: 1, maxWidth: 400 };
    if (photos.length === 2) return { gap: 8, cols: 2, maxWidth: 'none' };
    if (photos.length === 3) return { gap: 6, cols: 3, maxWidth: 'none' };
    return { gap: 4, cols: 2, maxWidth: 'none' }; // 4+ photos in 2x2 grid
  };
  
  const layout = getPhotoLayout();
  
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        width: '100%',
        p: 4,
        flexDirection: 'column'
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { 
            xs: layout.cols === 1 ? '1fr' : 'repeat(2, 1fr)',
            sm: layout.cols === 1 ? '1fr' : layout.cols === 2 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            md: `repeat(${Math.min(layout.cols, 3)}, 1fr)`
          },
          gap: { xs: 4, md: layout.gap },
          maxWidth: layout.maxWidth,
          mx: 'auto'
        }}
      >
        {photos && photos.length > 0 ? (
          photos.map((photo, index) => (
            <Box
              key={index}
              sx={{
                animation: `${index === 0 ? slideInLeft : slideInRight} 1s ease-out`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Avatar
                src={photo.vatu_id ? `/api/vatu/download/${photo.vatu_id}/photo` : '/assets/default-avatar.png'}
                alt={photo.vatu_name || `Photo ${index + 1}`}
                sx={{
                  width: { xs: 180, sm: 240, md: 280 },
                  height: { xs: 180, sm: 240, md: 280 },
                  border: '8px solid #7f4b2d',
                  boxShadow: '0 12px 40px rgba(127,75,45,0.3)',
                  animation: `${pulse} 3s ease-in-out infinite`,
                  animationDelay: `${index * 0.5}s`,
                  '& img': {
                    objectFit: photo.photo_object_config?.fit || 'cover',
                    objectPosition: photo.photo_object_config?.position || 'center'
                  }
                }}
              />
              {photo.vatu_name && (
                <Typography
                  variant="h5"
                  sx={{
                    mt: 2,
                    fontWeight: 700,
                    color: '#7f4b2d',
                    textAlign: 'center',
                    fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' }
                  }}
                >
                  {photo.vatu_name}
                </Typography>
              )}
              {photo.category && (
                <Typography
                  variant="body1"
                  sx={{
                    mt: 0.75,
                    color: '#2e7d32',
                    textAlign: 'center',
                    fontWeight: 600,
                    fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' }
                  }}
                >
                  {labels[lang].anthyakshari_winner + ' ' + (currentLabels[photo.category] || photo.category) + ' ' + labels[lang].batch}
                </Typography>
              )}
              {photo.pathasala_name && (
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.5,
                    color: '#666',
                    textAlign: 'center',
                    fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.9rem' }
                  }}
                >
                  {photo.pathasala_name}
                </Typography>
              )}
            </Box>
          ))
        ) : (
          <Typography variant="h5" color="text.secondary">
            {currentLabels.noData}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// Slide 2: Pariksha Batches
// Pre-process batches into displayable cards (max 6 vatus per card)
const createBatchCards = (batches, vatuAssignments) => {
  const cards = [];
  const VATUS_PER_CARD = 6;
  
  batches.forEach((batch) => {
    const vatus = vatuAssignments[batch._id] || [];
    
    if (vatus.length === 0) {
      // Empty batch - still show one card
      cards.push({
        batch,
        vatus: [],
        cardIndex: 0,
        totalCards: 1
      });
    } else {
      // Split vatus into chunks of 6
      const totalCards = Math.ceil(vatus.length / VATUS_PER_CARD);
      for (let i = 0; i < vatus.length; i += VATUS_PER_CARD) {
        cards.push({
          batch,
          vatus: vatus.slice(i, i + VATUS_PER_CARD),
          cardIndex: Math.floor(i / VATUS_PER_CARD),
          totalCards
        });
      }
    }
  });
  
  return cards;
};

const ParikshaBatchesSlide = ({ batches, vatuAssignments, lang, slideIndex, onTotalSlidesChange, onPageComplete }) => {
  const currentLabels = labels[lang];
  const [pageIndex, setPageIndex] = useState(0);
  
  // Create all cards (each card has max 6 vatus)
  const allCards = (!batches || batches.length === 0) ? [] : createBatchCards(batches, vatuAssignments);
  
  // Display 3 cards per page (fits nicely in the viewport without overlap)
  const CARDS_PER_PAGE = 3;
  const totalPages = Math.max(1, Math.ceil(allCards.length / CARDS_PER_PAGE));
  
  // Get cards for current page
  const cardsForPage = allCards.slice(pageIndex * CARDS_PER_PAGE, (pageIndex + 1) * CARDS_PER_PAGE);

  // Reset page index when batches change
  useEffect(() => {
    setPageIndex(0);
  }, [batches]);

  // Notify parent about total slides needed
  useEffect(() => {
    if (onTotalSlidesChange) {
      onTotalSlidesChange(slideIndex, totalPages);
    }
  }, [totalPages, slideIndex, onTotalSlidesChange]);

  // Auto-pagination: rotate through pages every 5 seconds, notify parent when all pages done
  useEffect(() => {
    const timer = setInterval(() => {
      setPageIndex(prevIndex => {
        const nextIndex = prevIndex + 1;
        if (nextIndex >= totalPages) {
          // All pages shown, notify parent to advance to next main slide
          if (onPageComplete) {
            onPageComplete();
          }
          return 0; // Reset for next time this slide is shown
        }
        return nextIndex;
      });
    }, 5000);

    return () => clearInterval(timer);
  }, [totalPages, onPageComplete]);
  
  // Handle empty data
  if (!batches || batches.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography variant="h5" color="text.secondary">{currentLabels.noData}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', p: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Typography 
        variant="h4" 
        sx={{ 
          textAlign: 'center', 
          mb: 2, 
          fontWeight: 700, 
          color: '#7f4b2d',
          animation: `${fadeIn} 0.5s ease-out`
        }}
      >
        {currentLabels.parikshaBatches}
      </Typography>
      
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: `repeat(${Math.min(cardsForPage.length, 3)}, 1fr)` 
          },
          gap: 3,
          maxWidth: 1400,
          mx: 'auto',
          flex: 1,
          alignContent: 'center',
          overflow: 'hidden'
        }}
      >
        {cardsForPage.map((cardData, cardIndex) => (
          <Paper
            key={`${cardData.batch._id}-${cardData.cardIndex}`}
            elevation={4}
            sx={{
              p: 3,
              borderRadius: 4,
              border: '3px solid #7f4b2d',
              background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(240,248,255,0.95) 100%)',
              animation: `${fadeIn} 0.6s ease-out`,
              animationDelay: `${cardIndex * 0.15}s`,
              animationFillMode: 'both',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 280,
              maxHeight: 380,
              boxShadow: '0 8px 32px rgba(127,75,45,0.15)'
            }}
          >
            {/* Card Header */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 2,
              pb: 2,
              borderBottom: '2px solid rgba(127,75,45,0.1)'
            }}>
              <Box>
                <Typography variant="h7" sx={{ fontWeight: 800, color: '#7f4b2d', lineHeight: 1.2 }}>
                  {cardData.batch.batch_name}
                </Typography>
                {cardData.totalCards > 1 && (
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>
                    Part {cardData.cardIndex + 1} of {cardData.totalCards}
                  </Typography>
                )}
              </Box>
              <Chip 
                label={cardData.batch.pariksha_type?.replace(/_/g, ' ').toUpperCase()} 
                size="small"
                sx={{ 
                  bgcolor: '#7f4b2d', 
                  color: '#fff',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  px: 1
                }} 
              />
            </Box>
            
            {/* Vatu Names - Large Font, 6 per card */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 1, 
              flex: 1,
              justifyContent: 'space-evenly',
              overflow: 'hidden',
              py: 0.5
            }}>
              {cardData.vatus.length > 0 ? (
                cardData.vatus.map((vatu, vIndex) => (
                  <Box
                    key={vatu.vatu_id || vIndex}
                    sx={{
                      bgcolor: 'rgba(127,75,45,0.08)',
                      borderRadius: 2,
                      px: 1.5,
                      py: 1,
                      animation: `${fadeIn} 0.4s ease-out`,
                      animationDelay: `${(cardIndex * 0.15) + (vIndex * 0.08)}s`,
                      animationFillMode: 'both',
                      border: '1px solid rgba(127,75,45,0.12)',
                      transition: 'all 0.2s ease',
                      flexShrink: 0,
                      minHeight: 0,
                      '&:hover': {
                        bgcolor: 'rgba(127,75,45,0.12)',
                        transform: 'translateX(4px)'
                      }
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#7f4b2d',
                        fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                        fontWeight: 700,
                        textAlign: 'center',
                        letterSpacing: '0.01em',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        lineHeight: 1.3
                      }}
                    >
                      {vatu.vatu_name}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
                  {currentLabels.noData}
                </Typography>
              )}
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

// Slide 3: Panasala Antyakshari Batches
// Pre-process antyakshari batches into displayable cards (max 6 vatus per card)
const createAntyakshariCards = (batches) => {
  const cards = [];
  const VATUS_PER_CARD = 6;
  
  batches.forEach((batch) => {
    const vatus = batch.vatus || [];
    
    if (vatus.length === 0) {
      cards.push({
        batch,
        vatus: [],
        cardIndex: 0,
        totalCards: 1
      });
    } else {
      const totalCards = Math.ceil(vatus.length / VATUS_PER_CARD);
      for (let i = 0; i < vatus.length; i += VATUS_PER_CARD) {
        cards.push({
          batch,
          vatus: vatus.slice(i, i + VATUS_PER_CARD),
          cardIndex: Math.floor(i / VATUS_PER_CARD),
          totalCards
        });
      }
    }
  });
  
  return cards;
};

const AntyakshariBatchesSlide = ({ batches, lang, slideIndex, onTotalSlidesChange, onPageComplete }) => {
  const currentLabels = labels[lang];
  const [pageIndex, setPageIndex] = useState(0);
  
  const juniorBatches = (batches || []).filter(b => b.batch_type === 'junior');
  const seniorBatches = (batches || []).filter(b => b.batch_type === 'senior');

  // Create cards for each type (max 6 vatus per card)
  const juniorCards = createAntyakshariCards(juniorBatches);
  const seniorCards = createAntyakshariCards(seniorBatches);
  
  // Show 2 cards per column per page (total 4 cards per page: 2 junior + 2 senior)
  const CARDS_PER_COLUMN = 2;
  
  const totalJuniorPages = Math.max(1, Math.ceil(juniorCards.length / CARDS_PER_COLUMN));
  const totalSeniorPages = Math.max(1, Math.ceil(seniorCards.length / CARDS_PER_COLUMN));
  const totalPages = Math.max(1, Math.max(totalJuniorPages, totalSeniorPages));

  const juniorCardsPage = juniorCards.slice(pageIndex * CARDS_PER_COLUMN, (pageIndex + 1) * CARDS_PER_COLUMN);
  const seniorCardsPage = seniorCards.slice(pageIndex * CARDS_PER_COLUMN, (pageIndex + 1) * CARDS_PER_COLUMN);

  // Reset page index when batches change
  useEffect(() => {
    setPageIndex(0);
  }, [batches]);

  // Notify parent about total slides needed
  useEffect(() => {
    if (onTotalSlidesChange) {
      onTotalSlidesChange(slideIndex, totalPages);
    }
  }, [totalPages, slideIndex, onTotalSlidesChange]);

  // Auto-pagination: rotate through pages every 5 seconds, notify parent when all pages done
  useEffect(() => {
    const timer = setInterval(() => {
      setPageIndex(prevIndex => {
        const nextIndex = prevIndex + 1;
        if (nextIndex >= totalPages) {
          // All pages shown, notify parent to advance to next main slide
          if (onPageComplete) {
            onPageComplete();
          }
          return 0; // Reset for next time this slide is shown
        }
        return nextIndex;
      });
    }, 5000);

    return () => clearInterval(timer);
  }, [totalPages, onPageComplete]);

  // Handle empty data
  if (!batches || batches.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography variant="h5" color="text.secondary">{currentLabels.noData}</Typography>
      </Box>
    );
  }

  const renderCardColumn = (cards, title, color) => (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Typography 
        variant="h5" 
        sx={{ 
          textAlign: 'center', 
          mb: 2, 
          fontWeight: 700, 
          color: color,
          borderBottom: `3px solid ${color}`,
          pb: 1
        }}
      >
        {title}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
        {cards.length > 0 ? cards.map((cardData, cardIndex) => (
          <Paper
            key={`${cardData.batch._id}-${cardData.cardIndex}`}
            elevation={3}
            sx={{
              p: 3,
              borderRadius: 3,
              border: `3px solid ${color}`,
              background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(250,250,255,0.95) 100%)',
              animation: `${fadeIn} 0.5s ease-out`,
              animationDelay: `${cardIndex * 0.15}s`,
              animationFillMode: 'both',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 6px 24px rgba(0,0,0,0.1)'
            }}
          >
            {/* Card Header */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 2,
              pb: 1.5,
              borderBottom: `1px solid ${color}30`
            }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: color }}>
                {currentLabels.batch} {cardData.batch.batch_number}
              </Typography>
              {cardData.totalCards > 1 && (
                <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>
                  ({cardData.cardIndex + 1}/{cardData.totalCards})
                </Typography>
              )}
            </Box>
            
            {/* Vatu Names - Larger Font */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 1, 
              flex: 1,
              justifyContent: 'space-evenly',
              overflow: 'hidden',
              py: 1
            }}>
              {cardData.vatus.length > 0 ? (
                cardData.vatus.map((vatu, vIndex) => (
                  <Box
                    key={vatu.vatu_id || vIndex}
                    sx={{
                      bgcolor: `${color}10`,
                      borderRadius: 2,
                      px: 1.5,
                      py: 1,
                      border: `1px solid ${color}20`,
                      animation: `${fadeIn} 0.3s ease-out`,
                      animationDelay: `${(cardIndex * 0.15) + (vIndex * 0.05)}s`,
                      animationFillMode: 'both',
                      transition: 'all 0.2s ease',
                      flexShrink: 0,
                      minHeight: 0,
                      '&:hover': {
                        bgcolor: `${color}18`,
                        transform: 'translateX(4px)'
                      }
                    }}
                  >
                    <Typography
                      sx={{
                        color: color,
                        fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.2rem' },
                        fontWeight: 700,
                        textAlign: 'center',
                        letterSpacing: '0.01em',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        lineHeight: 1.3
                      }}
                    >
                      {vatu.vatu_name}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
                  {currentLabels.noData}
                </Typography>
              )}
            </Box>
          </Paper>
        )) : (
          <Paper elevation={1} sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
            <Typography color="text.secondary">{currentLabels.noData}</Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ height: '100%', p: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Typography 
        variant="h4" 
        sx={{ 
          textAlign: 'center', 
          mb: 2, 
          fontWeight: 700, 
          color: '#7f4b2d',
          animation: `${fadeIn} 0.5s ease-out`
        }}
      >
        {currentLabels.antyakshariBatches}
      </Typography>
      
      <Box
        sx={{
          display: 'flex',
          gap: 4,
          maxWidth: 1400,
          mx: 'auto',
          flexDirection: { xs: 'column', md: 'row' },
          flex: 1,
          overflow: 'hidden'
        }}
      >
        {renderCardColumn(juniorCardsPage, currentLabels.junior, '#1565c0')}
        {renderCardColumn(seniorCardsPage, currentLabels.senior, '#c62828')}
      </Box>
    </Box>
  );
};

// Slide 4: Top 10 Performers
const TopPerformersSlide = ({ topVatus, lang, slideIndex, onTotalSlidesChange, onPageComplete }) => {
  const currentLabels = labels[lang];
  const [pageIndex, setPageIndex] = useState(0);
  
  // Calculate pagination: max 10 performers per page
  const performersPerPage = 10;
  const totalPages = Math.max(1, Math.ceil((topVatus || []).length / performersPerPage));
  const performersPage = (topVatus || []).slice(pageIndex * performersPerPage, (pageIndex + 1) * performersPerPage);

  // Reset page index when data changes
  useEffect(() => {
    setPageIndex(0);
  }, [topVatus]);

  // Notify parent about total slides needed
  useEffect(() => {
    if (onTotalSlidesChange) {
      onTotalSlidesChange(slideIndex, totalPages);
    }
  }, [totalPages, slideIndex, onTotalSlidesChange]);

  // Auto-pagination: rotate through pages every 5 seconds, notify parent when all pages done
  useEffect(() => {
    const timer = setInterval(() => {
      setPageIndex(prevIndex => {
        const nextIndex = prevIndex + 1;
        if (nextIndex >= totalPages) {
          // All pages shown, notify parent to advance to next main slide
          if (onPageComplete) {
            onPageComplete();
          }
          return 0; // Reset for next time this slide is shown
        }
        return nextIndex;
      });
    }, 5000);

    return () => clearInterval(timer);
  }, [totalPages, onPageComplete]);

  // Handle empty data
  if (!topVatus || topVatus.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography variant="h5" color="text.secondary">{currentLabels.noData}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', p: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Typography 
        variant="h6" 
        sx={{ 
          textAlign: 'center', 
          mb: 2, 
          fontWeight: 700, 
          color: '#7f4b2d',
          animation: `${fadeIn} 0.5s ease-out`
        }}
      >
        {currentLabels.topPerformers}
      </Typography>
      
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
          gap: 1.2,
          maxWidth: 1400,
          mx: 'auto',
          flex: 1,
          overflow: 'hidden',
          alignContent: 'center',
          alignItems: 'stretch'
        }}
      >
        {performersPage.map((vatu, index) => (
          <Paper
            key={vatu.vatu_id || index}
            elevation={3}
            sx={{
              p: 1.8,
              borderRadius: 2.5,
              textAlign: 'center',
              background: index < 3 
                ? `linear-gradient(135deg, ${index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : '#CD7F32'}20, rgba(255,255,255,0.95))`
                : 'rgba(255,255,255,0.95)',
              border: index < 3 
                ? `3px solid ${index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : '#CD7F32'}`
                : '2px solid #7f4b2d',
              animation: `${fadeIn} 0.5s ease-out`,
              animationDelay: `${index * 0.1}s`,
              animationFillMode: 'both',
              position: 'relative',
              overflow: 'visible',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            
            
            <Avatar
              src={vatu.photo_download_link || '/assets/default-avatar.png'}
              alt={vatu.vatu_name}
              sx={{
                width: { xs: 75, md: 95 },
                height: { xs: 75, md: 95 },
                mx: 'auto',
                mt: 0.8,
                border: '3px solid #7f4b2d',
                boxShadow: '0 4px 12px rgba(127,75,45,0.2)',
                '& img': {
                  objectFit: vatu.photo_object_config?.fit || 'cover',
                  objectPosition: vatu.photo_object_config?.position || 'center'
                }
              }}
            />
            
            <Typography 
              variant="subtitle1" 
              sx={{ 
                mt: 0.6, 
                fontWeight: 700, 
                color: '#7f4b2d',
                fontSize: { xs: '0.95rem', md: '1.15rem' },
                lineHeight: 1.15,
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {vatu.vatu_name}
            </Typography>
            
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#666',
                fontSize: { xs: '0.75rem', md: '0.85rem' },
                mt: 0.3,
                lineHeight: 1.1
              }}
            >
              {vatu.pathasala_name}
            </Typography>
            
            <Chip
              label={`${vatu.percentage}%`}
              sx={{
                mt: 0.6,
                bgcolor: '#7f4b2d',
                color: '#fff',
                fontWeight: 800,
                fontSize: { xs: '0.9rem', md: '0.95rem' },
                height: { xs: '28px', md: '32px' },
                '& .MuiChip-label': {
                  px: 1.2,
                  fontSize: 'inherit'
                }
              }}
            />
          </Paper>
        ))}
      </Box>
    </Box>
  );

};

const AdminSlideshow = () => {
  const { lang } = useLanguage();
  const { teluguYearTelugu, yearId } = useTeluguYear();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [parikshaBatches, setParikshaBatches] = useState([]);
  const [vatuAssignments, setVatuAssignments] = useState({});
  const [antyakshariBatches, setAntyakshariBatches] = useState([]);
  const [topVatus, setTopVatus] = useState([]);
  
  // Track pagination for each slide with sub-pages
  const [slidePageIndices, setSlidePageIndices] = useState({
    1: 0, // Pariksha batches page
    2: 0, // Antyakshari batches page
    3: 0  // Top performers page
  });

  // Track total pages for slides that have pagination
  const [slideTotalPages, setSlideTotalPages] = useState({
    1: 1,
    2: 1,
    3: 1
  });
  
  // Loading states for each slide
  const [loadingSlides, setLoadingSlides] = useState({
    0: false,
    1: false,
    2: false,
    3: false
  });

  const currentLabels = labels[lang];

  // Handle slide page changes for pagination
  const handleTotalSlidesChange = useCallback((slideIndex, totalPages) => {
    setSlideTotalPages(prev => ({
      ...prev,
      [slideIndex]: totalPages
    }));
  }, []);

  // Fetch data for Slide 0: Featured Photos
  const fetchFeaturedPhotosData = useCallback(async () => {
    if (!yearId) return;
    
    setLoadingSlides(prev => ({ ...prev, 0: true }));
    try {
      const photosRes = await api.get(`/api/vatu/slideshow/featured-photos?year=${encodeURIComponent(teluguYearTelugu)}`);
      const photosData = await photosRes.json();
      if (photosData.success && photosData.data) {
        setPhotos(photosData.data);
      }
    } catch (err) {
      console.error('Error fetching featured photos:', err);
    } finally {
      setLoadingSlides(prev => ({ ...prev, 0: false }));
    }
  }, [teluguYearTelugu]);

  // Fetch data for Slide 1: Pariksha Batches and Vatu Assignments
  const fetchParikshaBatchesData = useCallback(async () => {
    if (!teluguYearTelugu) return;
    
    setLoadingSlides(prev => ({ ...prev, 1: true }));
    try {
      const batchesRes = await api.get(`/api/pariksha-batch/year/${encodeURIComponent(teluguYearTelugu)}`);
      const batchesData = await batchesRes.json();
      if (batchesData.success) {
        setParikshaBatches(batchesData.data || []);
        const assignRes = await api.get(`/api/vatu-assignments/${teluguYearTelugu}`);
        const assignData = await assignRes.json();
        if (assignRes.ok && assignData.success) {
          const assignments = {};
          Object.entries(assignData.data).forEach(([batchId, batchData]) => {
            assignments[batchId] = batchData.vatus.map((v) => ({
              vatu_id: v.vatu._id,
              vatu_name: v.vatu.vatu_name
            }));
          });
          setVatuAssignments(assignments);
        }
      }
    } catch (err) {
      console.error('Error fetching pariksha batches data:', err);
    } finally {
      setLoadingSlides(prev => ({ ...prev, 1: false }));
    }
  }, [teluguYearTelugu]);

  // Fetch data for Slide 2: Antyakshari Batches
  const fetchAntyakshariBatchesData = useCallback(async () => {
    if (!teluguYearTelugu) return;
    
    setLoadingSlides(prev => ({ ...prev, 2: true }));
    try {
      const antyakshariRes = await api.get(`/api/panasala-antyakshari/batches/${encodeURIComponent(teluguYearTelugu)}`);
      const antyakshariData = await antyakshariRes.json();
      if (antyakshariData.success) {
        setAntyakshariBatches(antyakshariData.data || []);
      }
    } catch (err) {
      console.error('Error fetching antyakshari batches data:', err);
    } finally {
      setLoadingSlides(prev => ({ ...prev, 2: false }));
    }
  }, [teluguYearTelugu]);

  // Fetch data for Slide 3: Top Performers
  const fetchTopVatusData = useCallback(async () => {
    if (!teluguYearTelugu) return;
    
    setLoadingSlides(prev => ({ ...prev, 3: true }));
    try {
      const topRes = await api.get(`/api/result/top-vatus?year_id=${encodeURIComponent(yearId)}&pariksha_type=krishna_yajurveda&limit=10`);
      const topData = await topRes.json();
      if (topData.success) {
        setTopVatus(topData.data || []);
      }
    } catch (err) {
      console.error('Error fetching top vatus data:', err);
    } finally {
      setLoadingSlides(prev => ({ ...prev, 3: false }));
    }
  }, [yearId]);

  // Load data when slide changes
  useEffect(() => {
    if (currentSlide === 0) {
      fetchFeaturedPhotosData();
    } else if (currentSlide === 1) {
      fetchParikshaBatchesData();
    } else if (currentSlide === 2) {
      fetchAntyakshariBatchesData();
    } else if (currentSlide === 3) {
      fetchTopVatusData();
    }
  }, [currentSlide, fetchFeaturedPhotosData, fetchParikshaBatchesData, fetchAntyakshariBatchesData, fetchTopVatusData]);

  // Callback when a slide with pagination completes all its pages
  const handleSlidePageComplete = useCallback(() => {
    if (isPaused) return;
    setCurrentSlide((prev) => (prev + 1) % 4);
  }, [isPaused]);

  // Auto-advance for slides WITHOUT pagination (slide 0 - photos, slide 3 - top performers only if single page)
  // Slides 1 and 2 will call handleSlidePageComplete when they finish all their pages
  useEffect(() => {
    if (isPaused) return;
    
    // Only auto-advance for slide 0 (PhotoSlide) which doesn't have pagination
    // Slides 1, 2, 3 manage their own timing and call handleSlidePageComplete
    if (currentSlide === 0) {
      const timer = setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % 4);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isPaused, currentSlide]);

  // Handle fullscreen toggle
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Show loading spinner only for current slide if it's loading
  const isCurrentSlideLoading = loadingSlides[currentSlide];

  // Check if user has admin access - Admin only
  const hasAdminAccess = user && user.role === 'Admin';

  if (!hasAdminAccess) {
    return (
      <Box 
        sx={{ 
          height: '100vh', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          background: slideBackgrounds[0]
        }}
      >
        <Typography variant="h5" color="error">
          {lang === 'telugu' ? 'అనుమతి లేదు' : 'अनुमति नास्ति'}
        </Typography>
      </Box>
    );
  }

  const renderSlide = () => {
    // Show loading state for current slide if it's still loading
    if (isCurrentSlideLoading) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress size={60} sx={{ color: '#7f4b2d' }} />
          <Typography variant="h6" sx={{ mt: 3, color: '#7f4b2d' }}>
            {currentLabels.loading}
          </Typography>
        </Box>
      );
    }
    
    switch (currentSlide) {
      case 0:
        return <PhotoSlide photos={photos} lang={lang} />;
      case 1:
        return <ParikshaBatchesSlide 
          batches={parikshaBatches} 
          vatuAssignments={vatuAssignments} 
          lang={lang}
          slideIndex={1}
          onTotalSlidesChange={handleTotalSlidesChange}
          onPageComplete={handleSlidePageComplete}
        />;
      case 2:
        return <AntyakshariBatchesSlide 
          batches={antyakshariBatches} 
          lang={lang}
          slideIndex={2}
          onTotalSlidesChange={handleTotalSlidesChange}
          onPageComplete={handleSlidePageComplete}
        />;
      case 3:
        return <TopPerformersSlide 
          topVatus={topVatus} 
          lang={lang}
          slideIndex={3}
          onTotalSlidesChange={handleTotalSlidesChange}
          onPageComplete={handleSlidePageComplete}
        />;
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 1200,
        background: slideBackgrounds[currentSlide],
        transition: 'background 0.5s ease-in-out',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Custom AppBar for Slideshow */}
      <AppBar 
        position="fixed" 
        sx={{ 
          bgcolor: '#7f4b2d',
          boxShadow: '0 2px 10px rgba(127,75,45,0.3)'
        }}
      >
        <Toolbar>
          <Tooltip title={lang === 'telugu' ? 'వెనుకకు' : 'पृष्ठतः'}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => navigate('/admin')}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
            {lang === 'telugu' ? 'వేదారణ్యమ్ పరీక్ష ముఖ్యాంశాలు' : 'वेदारण्यम् परीक्षा मुख्यांशाः'}
          </Typography>
          
          <Tooltip title={isPaused ? (lang === 'telugu' ? 'ప్లే' : 'प्ले') : (lang === 'telugu' ? 'పాజ్' : 'पॉज़')}>
            <IconButton color="inherit" onClick={() => setIsPaused(!isPaused)}>
              {isPaused ? <PlayArrowIcon /> : <PauseIcon />}
            </IconButton>
          </Tooltip>
          
          <Tooltip title={isFullscreen ? (lang === 'telugu' ? 'నిష్క్రమణ' : 'निर्गमनम्') : (lang === 'telugu' ? 'పూర్తి స్క్రీన్' : 'पूर्ण पटलम्')}>
            <IconButton color="inherit" onClick={toggleFullscreen}>
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      
      {/* Slide content - with padding top for AppBar */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          mt: '64px' // AppBar height
        }}
      >
        {renderSlide()}
      </Box>

      {/* Slide indicators */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 1.5,
          pb: 3,
          pt: 2
        }}
      >
        {[0, 1, 2, 3].map((index) => (
          <Box
            key={index}
            onClick={() => setCurrentSlide(index)}
            sx={{
              width: currentSlide === index ? 32 : 12,
              height: 12,
              borderRadius: 6,
              bgcolor: currentSlide === index ? '#7f4b2d' : 'rgba(127,75,45,0.3)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: currentSlide === index ? '#7f4b2d' : 'rgba(127,75,45,0.5)'
              }
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default AdminSlideshow;
